import { passedOrFailed } from "~/lib/assessment";

// ── Types ─────────────────────────────────────────────────────────────────────

export type SkillLevelTier = "INSHORE" | "COASTAL" | "OFFSHORE" | "OCEAN" | "PROFESSIONAL";
export type AssessmentResult = "passed" | "failed" | "partial";

export interface SkillAssessment {
  id: string;
  skillName: string;
  category: string;
  tier: SkillLevelTier;
  result: AssessmentResult;
  ratingLabel: string;
  assessor: string;
  expedition: string;
  date: string;
  notes?: string;
}

export interface SkillPathCategory {
  name: string;
  total: number;
  passed: number;
  assessments: SkillAssessment[];
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const TIER_ORDER: SkillLevelTier[] = ["INSHORE", "COASTAL", "OFFSHORE", "OCEAN", "PROFESSIONAL"];

export const TIER_CONFIG: Record<SkillLevelTier, { label: string; color: string; bar: string; ring: string }> = {
  INSHORE: { label: "Inshore", color: "text-shallow-700", bar: "bg-shallow-500", ring: "ring-shallow-400" },
  COASTAL: { label: "Coastal", color: "text-abyss-600", bar: "bg-abyss-500", ring: "ring-abyss-400" },
  OFFSHORE: { label: "Offshore", color: "text-violet-600", bar: "bg-violet-500", ring: "ring-violet-400" },
  OCEAN: { label: "Ocean", color: "text-signal-700", bar: "bg-signal-500", ring: "ring-signal-400" },
  PROFESSIONAL: { label: "Professional", color: "text-coral-700", bar: "bg-coral-500", ring: "ring-coral-400" },
};

export const RESULT_CONFIG: Record<AssessmentResult, { label: string; className: string }> = {
  passed: { label: "Pass", className: "bg-success-muted text-success-dark" },
  failed: { label: "Fail", className: "bg-danger-muted text-danger-dark" },
  partial: { label: "Partial", className: "bg-warning-muted text-warning-dark" },
};

// ── Input shape ───────────────────────────────────────────────────────────────

// Structural duck-type — any query fragment providing these fields satisfies it.
interface RawSkillInput {
  id: string;
  passed: boolean;
  ratingValue: string;
  assessedAt: string;
  notes?: string | null | undefined;
  skill: {
    name: string;
    description?: string | null | undefined;
    category: { name: string };
  };
  assessor: { displayName: string };
  crewMembership?: { expedition: { name: string } } | null | undefined;
}

// ── Functions ─────────────────────────────────────────────────────────────────

export function mapTier(levelName: string | null | undefined): SkillLevelTier {
  const upper = (levelName ?? "").toUpperCase();
  if (upper.includes("INSHORE") || upper.includes("BASIC")) return "INSHORE";
  if (upper.includes("OFFSHORE")) return "OFFSHORE";
  if (upper.includes("OCEAN")) return "OCEAN";
  if (upper.includes("PROFESSIONAL") || upper.includes("COMMERCIAL")) return "PROFESSIONAL";
  return "COASTAL";
}

export function buildSkillCategories(assessments: RawSkillInput[]): SkillPathCategory[] {
  const byCategory = new Map<string, SkillPathCategory>();
  for (const a of assessments) {
    const catName = a.skill.category.name;
    if (!byCategory.has(catName)) {
      byCategory.set(catName, { name: catName, total: 0, passed: 0, assessments: [] });
    }
    const cat = byCategory.get(catName);
    if (!cat) continue;
    cat.total++;
    const result: AssessmentResult = passedOrFailed(a.passed);
    if (result === "passed") cat.passed++;
    cat.assessments.push({
      id: a.id,
      skillName: a.skill.name,
      category: catName,
      tier: mapTier(a.skill.description),
      result,
      ratingLabel: a.ratingValue,
      assessor: a.assessor.displayName,
      expedition: a.crewMembership?.expedition.name ?? "—",
      date: new Date(a.assessedAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
      ...(a.notes ? { notes: a.notes } : {}),
    });
  }
  return [...byCategory.values()];
}
